import * as React from 'react';

import { TextArea, Select, Switch, TextField } from '@rematter/ui-web';
import { UserCheck, Clock, AlertCircle } from 'lucide-react';

import { NodeConfigProps } from '../../types';

import { ApprovalConfigType } from './schema';

import { MultiSelectUser } from '~/shared/components/select-fields/MultiSelectUser';

/**
 * Configuration panel for Approval nodes.
 * Allows configuring approvers, instructions, and optional timeout.
 */
export const ApprovalConfig: React.FC<NodeConfigProps<ApprovalConfigType>> = ({
  config,
  onUpdate,
}) => {
  const [userIds, setUserIds] = React.useState<string[]>(
    config.approvers?.userIds || [],
  );
  const [roleIds, setRoleIds] = React.useState<string>(
    config.approvers?.roleIds?.join(', ') || '',
  );
  const [instructions, setInstructions] = React.useState<string>(
    config.instructions || '',
  );
  const [timeoutEnabled, setTimeoutEnabled] = React.useState<boolean>(
    config.timeoutEnabled || false,
  );
  const [timeoutDuration, setTimeoutDuration] = React.useState<string>(
    config.timeoutDuration?.toString() || '24',
  );
  const [timeoutUnit, setTimeoutUnit] = React.useState<string>(
    config.timeoutUnit || 'hours',
  );
  const [timeoutAction, setTimeoutAction] = React.useState<string>(
    config.timeoutAction || 'reject',
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<ApprovalConfigType>) => {
      onUpdate({ ...config, ...updates });
    },
    [config, onUpdate],
  );

  const handleUserIdsChange = React.useCallback(
    (newUserIds: string[]) => {
      setUserIds(newUserIds);
      handleUpdate({
        approvers: {
          ...config.approvers,
          userIds: newUserIds,
          roleIds: config.approvers?.roleIds || [],
        },
      });
    },
    [config.approvers, handleUpdate],
  );

  const handleRoleIdsChange = React.useCallback(
    (value: string) => {
      setRoleIds(value);
      const ids = value
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
      handleUpdate({
        approvers: {
          ...config.approvers,
          userIds: config.approvers?.userIds || [],
          roleIds: ids,
        },
      });
    },
    [config.approvers, handleUpdate],
  );

  const handleInstructionsChange = React.useCallback(
    (value: string) => {
      setInstructions(value);
      handleUpdate({ instructions: value });
    },
    [handleUpdate],
  );

  const handleTimeoutEnabledChange = React.useCallback(
    (enabled: boolean) => {
      setTimeoutEnabled(enabled);
      handleUpdate({ timeoutEnabled: enabled });
    },
    [handleUpdate],
  );

  const handleTimeoutDurationChange = React.useCallback(
    (value: string) => {
      setTimeoutDuration(value);
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        handleUpdate({ timeoutDuration: numValue });
      }
    },
    [handleUpdate],
  );

  const handleTimeoutUnitChange = React.useCallback(
    (value: string) => {
      setTimeoutUnit(value);
      handleUpdate({ timeoutUnit: value as 'hours' | 'days' });
    },
    [handleUpdate],
  );

  const handleTimeoutActionChange = React.useCallback(
    (value: string) => {
      setTimeoutAction(value);
      handleUpdate({ timeoutAction: value as 'reject' | 'approve' });
    },
    [handleUpdate],
  );

  return (
    <div className="@ApprovalConfig space-y-4">
      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Note:</strong> Specify user IDs and/or role IDs who can
          approve or reject this step. At least one approver is required.
        </p>
      </div>

      <div>
        <MultiSelectUser
          label="Approver Users"
          value={userIds}
          onChange={handleUserIdsChange}
          placeholder="Select users..."
        />
      </div>

      <div>
        <TextField
          label="Approver Role IDs"
          value={roleIds}
          onChange={handleRoleIdsChange}
          placeholder="role-id-1, role-id-2"
          description="Comma-separated list of role IDs who can approve"
        />
      </div>

      <div>
        <TextArea
          label="Instructions (Optional)"
          value={instructions}
          onChange={handleInstructionsChange}
          placeholder="Please review and approve this request..."
          description="Instructions or context for approvers"
          rows={3}
        />
      </div>

      <div className="border-t pt-4">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="size-4 text-neutral/60" />
          <p className="text-sm font-medium">Timeout Settings</p>
        </div>

        <div className="mb-3">
          <Switch
            isSelected={timeoutEnabled}
            onChange={handleTimeoutEnabledChange}
          >
            <span className="ml-2 text-sm">Enable timeout</span>
          </Switch>
          <p className="mt-1 text-xs text-neutral/60">
            Automatically approve or reject if no response within specified time
          </p>
        </div>

        {timeoutEnabled && (
          <div className="space-y-3 rounded-lg border bg-neutral/5 p-3">
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Duration"
                value={timeoutDuration}
                onChange={handleTimeoutDurationChange}
                placeholder="24"
                type="number"
              />

              <div>
                <label className="mb-1 block text-sm font-medium">Unit</label>
                <Select
                  placeholder="Select unit..."
                  value={timeoutUnit}
                  onChange={handleTimeoutUnitChange}
                  options={[
                    { key: 'hours', label: 'Hours' },
                    { key: 'days', label: 'Days' },
                  ]}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">
                Timeout Action
              </label>
              <Select
                placeholder="Select action..."
                value={timeoutAction}
                onChange={handleTimeoutActionChange}
                options={[
                  { key: 'reject', label: 'Auto-reject' },
                  { key: 'approve', label: 'Auto-approve' },
                ]}
              />
              <p className="mt-1 text-xs text-neutral/60">
                Action to take when timeout is reached
              </p>
            </div>

            {timeoutDuration && timeoutUnit && (
              <div className="flex items-center gap-2 rounded-lg border border-warning/20 bg-warning/10 p-2">
                <AlertCircle className="size-4 text-warning" />
                <span className="text-xs text-warning">
                  Will {timeoutAction === 'approve' ? 'approve' : 'reject'}{' '}
                  after {timeoutDuration} {timeoutUnit} if no response
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Approval Flow:</p>
        <div className="space-y-1 text-xs text-neutral/70">
          <div>
            1. Automation pauses at this step and creates an approval task
          </div>
          <div>2. Specified approvers receive notification to review</div>
          <div>3. First approver to respond determines the outcome</div>
          <div>
            4. Flow continues down "approved" or "rejected" branch accordingly
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> You can add different actions on each branch.
          For example, send an email on approval and create a task on rejection.
        </p>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-success/20 bg-success/10 p-3">
        <UserCheck className="size-4 text-success" />
        <span className="text-xs text-success">
          Approved branch continues when approved
        </span>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-failure/20 bg-failure/10 p-3">
        <AlertCircle className="size-4 text-failure" />
        <span className="text-xs text-failure">
          Rejected branch continues when rejected
        </span>
      </div>
    </div>
  );
};
